import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from './LoadingSpinner';

/**
 * Bloqueio de rota por papel. `blocked` lista quem NÃO entra; `allowPermission`
 * reabre para quem tem a permissão específica.
 *
 * ⚠️ O PONTO DELICADO É O PERFIL AUSENTE.
 *
 * A versão anterior era `if (profile?.role && blocked.includes(...))`. Quando
 * `profile` era null, a condição inteira dava falso e a tela era renderizada —
 * o guarda falhava ABERTO. E `profile` fica null com mais frequência do que
 * parece: o `AuthContext` destrava a interface por timeout de 8 segundos mesmo
 * sem ter conseguido carregar o perfil, e nesse estado qualquer pessoa logada
 * enxergava qualquer rota protegida.
 *
 * Agora falha FECHADO: sem papel comprovado, ninguém passa. É a direção certa
 * para um guarda — o custo de errar para o lado seguro é alguém ver o painel em
 * vez da tela que queria; o custo de errar para o outro lado é um vendedor
 * abrindo o financeiro.
 *
 * ⚠️ Consequência que vale conhecer: se o carregamento do perfil falhar de
 * verdade, a pessoa fica limitada ao painel até recarregar a página. Antes ela
 * navegava normalmente — e é justamente isso que era o problema.
 *
 * Isto continua sendo UX. A autorização real é a RLS do banco: mesmo passando
 * daqui, ninguém lê o que não pode.
 *
 * `allowed` é o inverso de `blocked`: lista quem ENTRA. Use quando a tela é de
 * poucos papéis — uma lista de bloqueio "todos menos adm" deixa passar qualquer
 * papel criado depois (foi o caso de `geradoras_externo`, que nem está no
 * `UserRole` e escapa de toda lista escrita antes dele).
 */
const RoleGuard = ({
  blocked = [],
  allowed,
  allowPermission,
  allowIf,
  children,
}: {
  blocked?: string[];
  allowed?: string[];
  allowPermission?: string;
  /** Reabre para um caso que papel e permissão não descrevem (ex.: vendedor de um departamento). */
  allowIf?: (profile: NonNullable<ReturnType<typeof useAuth>['profile']>) => boolean;
  children: React.ReactNode;
}) => {
  const { profile, loading } = useAuth();

  // Enquanto carrega não há decisão a tomar. Na prática o `ProtectedRoute` já
  // segurou aqui em cima; a checagem existe para o caso de alguém usar este
  // componente fora dele um dia.
  if (loading) return <LoadingSpinner label="Verificando acesso..." size="lg" fullPage />;

  // Sem papel, não dá para provar que a pessoa pode entrar. O painel é o
  // destino mais restrito que existe, e é para onde um papel bloqueado já ia.
  if (!profile?.role) return <Navigate to="/" replace />;

  const foraDaLista = allowed ? !allowed.includes(profile.role) : false;

  if (foraDaLista || blocked.includes(profile.role)) {
    if (allowPermission && profile.permissions?.includes(allowPermission)) {
      return <>{children}</>;
    }
    if (allowIf && allowIf(profile)) {
      return <>{children}</>;
    }
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

export default RoleGuard;
